import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../../layouts/MainLayout';
import {
  getUnmatchedWhatsapps,
  ignoreUnmatchedWhatsapp,
} from '../../services/whatsappService';
import UnmatchedWhatsappFilters from './components/UnmatchedWhatsappFilters';
import UnmatchedWhatsappsList from './components/UnmatchedWhatsappsList';
import UnmatchedWhatsappPreview from './components/UnmatchedWhatsappPreview';
import AssignWhatsappModal from './components/AssignWhatsappModal';
import Pagination from '../../components/Pagination';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

const UnmatchedWhatsappsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [whatsapps, setWhatsapps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [toAssign, setToAssign] = useState(null);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, total: 0 });

  const currentPage = parseInt(searchParams.get('page')) || 1;
  const userToken = localStorage.getItem('token');

  const fetchWhatsapps = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20,
        status: searchParams.get('status') || 'pending',
      };
      const search = searchParams.get('search');
      if (search) params.search = search;
      // Solo cuenta para el admin; a la empresa y al gestor el servidor les da
      // siempre lo de sus números de AR.
      const phase = searchParams.get('phase');
      if (phase) params.phase = phase;
      const startDate = searchParams.get('startDate');
      if (startDate) params.startDate = startDate;
      const endDate = searchParams.get('endDate');
      if (endDate) params.endDate = endDate;

      const response = await getUnmatchedWhatsapps(params);
      setWhatsapps(response.whatsapps || []);
      setPagination(response.pagination || {});
      if (response.whatsapps?.length > 0 && !selected) {
        setSelected(response.whatsapps[0]);
      }
    } catch (error) {
      console.error('Error fetching unmatched whatsapps:', error);
      if (error.response?.status === 403) {
        window.location = '/dashboard';
      }
      toast.error('Error al cargar los WhatsApps');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userToken) {
      window.location = '/login';
      return;
    }
    fetchWhatsapps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, userToken]);

  const handlePageChange = page => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page);
    setSearchParams(newParams);
  };

  const handleAssigned = () => {
    toast.success('WhatsApp asignado correctamente');
    setToAssign(null);
    setSelected(null);
    fetchWhatsapps();
  };

  const handleIgnore = async id => {
    if (!window.confirm('¿Seguro que quieres ignorar este WhatsApp?')) return;
    try {
      await ignoreUnmatchedWhatsapp(id);
      toast.success('WhatsApp marcado como ignorado');
      setSelected(null);
      fetchWhatsapps();
    } catch (error) {
      console.error('Error ignoring whatsapp:', error);
      toast.error('Error al ignorar el WhatsApp');
    }
  };

  return (
    <MainLayout>
      <div className='sm:flex sm:items-center sm:justify-between mb-8'>
        <div className='mb-4 sm:mb-0'>
          <div className='flex items-center'>
            <div className='mr-4'>
              <ChatBubbleLeftRightIcon className='w-12 h-12 text-green-600' />
            </div>
            <div>
              <h1 className='text-2xl font-semibold text-slate-900'>
                WhatsApps sin asignar
              </h1>
              <p className='mt-1.5 text-sm text-slate-600'>
                Gestiona los WhatsApps entrantes que no se han podido vincular automáticamente a un expediente
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className='bg-white shadow-lg rounded-lg border border-slate-200'>
        <div className='px-5 py-4 border-b border-slate-100'>
          <UnmatchedWhatsappFilters />
        </div>

        <div className='flex min-h-[calc(100vh-350px)]'>
          <div className='w-1/3 border-r border-slate-200 overflow-y-auto'>
            {loading ? (
              <div className='text-center py-12'>
                <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-500'></div>
                <p className='text-slate-500 mt-2'>Cargando...</p>
              </div>
            ) : whatsapps.length === 0 ? (
              <div className='text-center py-12'>
                <ChatBubbleLeftRightIcon className='w-12 h-12 text-gray-300 mx-auto mb-3' />
                <p className='text-slate-500'>No hay WhatsApps pendientes</p>
              </div>
            ) : (
              <UnmatchedWhatsappsList
                whatsapps={whatsapps}
                selectedId={selected?._id}
                onSelect={setSelected}
              />
            )}
          </div>

          <div className='flex-1 overflow-hidden'>
            <UnmatchedWhatsappPreview
              whatsapp={selected}
              onAssign={setToAssign}
              onIgnore={handleIgnore}
            />
          </div>
        </div>

        {pagination.totalPages > 1 && (
          <div className='px-5 py-4 border-t border-slate-100'>
            <div className='flex justify-between items-center'>
              <p className='text-sm text-gray-500'>
                Mostrando {whatsapps.length} de {pagination.total} WhatsApps
              </p>
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        )}
      </div>

      {toAssign && (
        <AssignWhatsappModal
          whatsapp={toAssign}
          onClose={() => setToAssign(null)}
          onAssigned={handleAssigned}
        />
      )}
    </MainLayout>
  );
};

export default UnmatchedWhatsappsPage;
